import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { ApolloProvider } from 'react-apollo';

import { fg } from '@atlaskit/platform-feature-flags';
import { ExperienceTrackerContext } from '@atlassian/experience-tracker';
import {
	AnalyticsProvider,
	DialogsProvider,
	I18nProvider,
	statusCodeHandlersProvider,
	useOrg,
	useSite,
	useTeamCentralStoreApi,
} from '@atlassian/ptc-common';
import { useJiraCheckService } from '@atlassian/team-work';
import { type AppConfigProviderState, TeamsWrapper } from '@atlassian/teams';
import { addBreadcrumb, logErrorMessage } from '@atlassian/teams-client/sentry';
import { getConfig } from '@atlassian/teams-common/context-config';
import { useLicenseCheck } from '@atlassian/teams-common/use-license-check';

import messagesConfig from '../../i18n/messagesConfig';
import AppRoutes from '../../routes';
import { type AppConfigProviderProps } from '../AppConfigContext';
import AppConfigProvider from '../AppConfigContext/AppConfigProvider';
import { ErrorBoundaryApp } from '../ErrorBoundary';

import * as Styled from './App.styled';
import AppInitialDataRequests from './AppInitialDataRequests';
import { type AppProps } from './types';

export default function App(props: AppProps) {
	const {
		appName,
		analytics,
		apolloClient,
		contextSpaPaths,
		contextEntryPoint,
		replace,
		push,
		locale,
		skipI18nProvider,
		addFlag,
		docTitle,
		env,
		history,
		statusCodeHandlers,
		cloudId,
		orgId,
		teamCentralHandlers,
		productFeatureFlags,
		invitePeopleHandlers,
		isAIEnabled,
	} = props;

	const [, { setCloudId }] = useSite();
	const [, { setOrgId }] = useOrg();
	const [, { initTeamCentral }] = useTeamCentralStoreApi();
	const orgIdLoadingTimer = useRef<NodeJS.Timeout | null>(null);
	const cloudIdLoadingTimer = useRef<NodeJS.Timeout | null>(null);

	const { isJiraAvailable } = useJiraCheckService(cloudId);
	// when FG off, set cloud id to empty string to skip api call
	const { data: licenseCheckResult } = useLicenseCheck(
		fg('enable_site_url_context_in_teams') ? cloudId : '',
	);

	const expTracker = analytics.expTracker();

	const analyticsClient = analytics.getAnalyticsWebClient();

	useEffect(() => {
		if (orgId) {
			// @ts-expect-error - Property 'setOrgInfo' does not exist on type 'Promise<AnalyticsWebClient>'
			analyticsClient?.setOrgInfo(orgId);
		}
	}, [analyticsClient, orgId]);

	const appConfig = useMemo<AppConfigProviderState & AppConfigProviderProps>(
		() => ({
			env,
			appName,
			contextSpaPaths,
			contextEntryPoint,
			push,
			replace,
			addFlag,
			locale,
			docTitle,
			cloudId,
			orgId,
			productFeatureFlags,
			stargateRoot: getConfig().stargateRoot,
			isJiraAvailable,
			isAIEnabled,
			siteUrl: licenseCheckResult?.hostname,
		}),
		[
			env,
			appName,
			contextSpaPaths,
			contextEntryPoint,
			isJiraAvailable,
			push,
			replace,
			addFlag,
			locale,
			docTitle,
			cloudId,
			orgId,
			productFeatureFlags,
			isAIEnabled,
			licenseCheckResult?.hostname,
		],
	);

	// init statusCodeHandlersProvider
	useEffect(() => {
		if (statusCodeHandlers) {
			statusCodeHandlersProvider.setHandlers(statusCodeHandlers);
		}
	}, [statusCodeHandlers]);

	// save cloud id passed from consumers to a remote state management.
	useEffect(() => {
		setCloudId(cloudId);

		if (!cloudId) {
			cloudIdLoadingTimer.current = setTimeout(() => {
				logErrorMessage('No cloudId set - App.tsx');
			}, 3000);
		} else {
			if (cloudIdLoadingTimer.current) {
				clearInterval(cloudIdLoadingTimer.current);
			}
		}
	}, [cloudId, setCloudId]);

	// save org id passed from consumers to a remote state management.
	useEffect(() => {
		setOrgId(orgId);

		if (!orgId) {
			orgIdLoadingTimer.current = setTimeout(() => {
				logErrorMessage('No orgId set - App.tsx');
			}, 3000);
		} else {
			if (orgIdLoadingTimer.current) {
				clearInterval(orgIdLoadingTimer.current);
			}
		}
	}, [orgId, setOrgId]);

	// Init and save various team central related data and preconditions
	useEffect(() => {
		initTeamCentral(appName, cloudId, teamCentralHandlers);
	}, [appName, cloudId, teamCentralHandlers, initTeamCentral]);

	const handleTransition = useCallback(
		(pathname: string) => {
			if (expTracker) {
				expTracker.abort({ reason: 'Route changes.' });
			}
			addBreadcrumb(pathname);
		},
		[expTracker],
	);

	return (
		<I18nProvider
			messagesConfig={messagesConfig}
			skip={typeof skipI18nProvider !== 'undefined' ? skipI18nProvider : !locale}
			locale={locale}
		>
			<ErrorBoundaryApp analytics={analytics} appName={appName}>
				<TeamsWrapper appConfig={appConfig}>
					<AppConfigProvider
						env={env}
						contextSpaPaths={contextSpaPaths}
						contextEntryPoint={contextEntryPoint}
						docTitle={docTitle}
						appName={appName}
						addFlag={addFlag}
						push={push}
						locale={locale}
						replace={replace}
						history={history}
						cloudId={cloudId}
						orgId={orgId}
						isJiraAvailable={isJiraAvailable}
						teamCentralHandlers={teamCentralHandlers}
						productFeatureFlags={productFeatureFlags}
						invitePeopleHandlers={invitePeopleHandlers}
						isAIEnabled={isAIEnabled}
						siteUrl={licenseCheckResult?.hostname}
					>
						<AnalyticsProvider analytics={analytics}>
							<ExperienceTrackerContext.Provider value={analytics.expTracker()}>
								<Styled.RelativeWrapperOfHeaderImage>
									<ApolloProvider client={apolloClient}>
										<DialogsProvider>
											<AppInitialDataRequests appName={appName}>
												<AppRoutes
													contextEntryPoint={contextEntryPoint}
													history={history}
													onTransition={handleTransition}
												/>
											</AppInitialDataRequests>
										</DialogsProvider>
									</ApolloProvider>
								</Styled.RelativeWrapperOfHeaderImage>
							</ExperienceTrackerContext.Provider>
						</AnalyticsProvider>
					</AppConfigProvider>
				</TeamsWrapper>
			</ErrorBoundaryApp>
		</I18nProvider>
	);
}
